import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PATH } from 'src/features/navigation/utils/constants';
import { useCopyPricingPlan } from 'src/features/pricingPlan/api/useCopyPricingPlan';
import { LIST_PRICING_PLANS_RESOURCE } from 'src/features/pricingPlan/api/useListPricingPlans';
import PricingPlanFormModal from 'src/features/pricingPlan/components/form/PricingPlanFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface CopyPricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan: PricingPlan;
    children?: React.ReactNode;
}

function CopyPricingPlanFormModal<TData>({
    className,
    semantics,
    children,
    selectedPricingPlan,
}: CopyPricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const { mutate, isPending } = useCopyPricingPlan({
        onSuccess: (newPlan) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.copyPricingPlanSuccess),
                description: formatMessage(pricingPlanMessages.copyPricingPlanSuccessDescription, {
                    entry: selectedPricingPlan.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
            // Land the user on the freshly created plan's dashboard.
            void navigate(`/${PATH.ADMINISTRATION}/pricing-plan/${newPlan.id}`);
        },
    });

    return (
        <PricingPlanFormModal
            selectedPricingPlan={selectedPricingPlan}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ ...data, parkingId, id: selectedPricingPlan.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.copyEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.COPY}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.copyPricingPlanSubtitle, {
                entry: selectedPricingPlan.translations.find((translation) => translation.language === locale)?.name,
            })}
            isNewVersion={true}
        >
            {children}
        </PricingPlanFormModal>
    );
}

export default CopyPricingPlanFormModal;
